import React from 'react';
import LibMarkdown from 'markdown-to-jsx';
import styled from 'styled-components';

import { Box, ExternalLink, Link, Text, Theme } from 'baukasten';

import { useAiCompanionSidebar } from '../AiCompanionSidebarContext';
import { toInternalPath } from '../utils/answerLinks';

const Answer = styled(Box)`
  color: ${Theme.getColor('navy')};
  font-size: ${Theme.getFontSize(14)};
  line-height: 1.5;
  overflow-wrap: anywhere;

  > * + * {
    margin-top: ${Theme.getBaseUnit(3)};
  }
`;

const Paragraph = styled(Text)`
  line-height: 1.5;
`;

const List = styled(Box)`
  padding-left: ${Theme.getBaseUnit(5)};

  li + li {
    margin-top: ${Theme.getBaseUnit(1)};
  }
`;

const Code = styled(Box)`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: ${Theme.getFontSize(12)};
  background-color: ${Theme.getColor('grey-light-50')};
  border-radius: 4px;
  padding: 0 ${Theme.getBaseUnit(1)};
`;

const CodeBlock = styled(Box)`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: ${Theme.getFontSize(12)};
  background-color: ${Theme.getColor('grey-light-50')};
  border-radius: 8px;
  padding: ${Theme.getBaseUnit(3)};
  overflow-x: auto;
  white-space: pre;
`;

const TableScroller = styled(Box)`
  overflow-x: auto;
  border: 1px solid ${Theme.getColor('grey-light-100')};
  border-radius: 8px;
`;

// baukasten's Table is datagrid-shaped (52px header rows, hover outlines, no cell
// padding), so answer tables get their own plain elements
const Grid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${Theme.getFontSize(12)};

  th,
  td {
    padding: ${Theme.getBaseUnit(2)} ${Theme.getBaseUnit(3)};
    text-align: left;
    vertical-align: top;
    white-space: nowrap;
  }

  th {
    font-weight: ${Theme.getFontWeight('semibold')};
    color: ${Theme.getColor('grey-dark')};
    border-bottom: 1px solid ${Theme.getColor('grey-light-100')};
  }

  tbody tr + tr td {
    border-top: 1px solid ${Theme.getColor('grey-light-50')};
  }
`;

const Quote = styled(Box)`
  border-left: 2px solid ${Theme.getColor('grey-light-100')};
  padding-left: ${Theme.getBaseUnit(3)};
  color: ${Theme.getColor('grey-dark')};
`;

type ChatTableProps = { children?: React.ReactNode };

// markdown-to-jsx hands the parts down individually, so the scroller wraps the table here
const ChatTable = ({ children }: ChatTableProps) => (
  <TableScroller>
    <Grid>{children}</Grid>
  </TableScroller>
);

type ChatLinkProps = { href?: string; children?: React.ReactNode };

const ChatLink = ({ href, children }: ChatLinkProps) => {
  const { mode, setMode } = useAiCompanionSidebar();
  const url = href ?? '';
  const internalPath = toInternalPath(url);

  if (!internalPath) {
    return (
      <ExternalLink url={url} fontSize={14} showExternalIcon={false}>
        {children}
      </ExternalLink>
    );
  }

  // Link navigates on its own; the click bubbles up so the page is visible behind the chat
  return (
    <Box as="span" onClick={() => mode === 'fullscreen' && setMode('sidebar')}>
      <Link url={internalPath} fontSize={14}>
        {children}
      </Link>
    </Box>
  );
};

const heading = (as: 'h1' | 'h2' | 'h3' | 'h4', fontSize: 16 | 14) => ({
  component: Text,
  props: { as, fontSize, fontWeight: 'bold' as const },
});

const options = {
  wrapper: Answer,
  forceWrapper: true,
  disableParsingRawHTML: true,
  overrides: {
    p: { component: Paragraph, props: { as: 'p' } },
    a: ChatLink,
    // a model-authored image URL is fetched on render — an exfiltration channel, so
    // answers never render images
    img: () => null,
    ul: { component: List, props: { as: 'ul' } },
    ol: { component: List, props: { as: 'ol' } },
    h1: heading('h1', 16),
    h2: heading('h2', 16),
    h3: heading('h3', 14),
    h4: heading('h4', 14),
    code: { component: Code, props: { as: 'code', display: 'inline' } },
    pre: { component: CodeBlock, props: { as: 'pre' } },
    blockquote: { component: Quote, props: { as: 'blockquote' } },
    table: ChatTable,
  },
};

type Props = { text: string };

export const ChatMarkdown = ({ text }: Props) => (
  <LibMarkdown options={options}>{text}</LibMarkdown>
);
